import React, { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Const from '../../utils/Const';
import { GetKeyEndTypes, GetOrgId } from '../../utils/UtilFunction';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { Dropdown, DropdownMulti } from '../../components/dropdown';
import { useGetDashboardData, useGetDropdownDataAuto } from './api/fetchData';
import { GraphAreaTraining } from './GraphAreaTraining';
import Button from '@mui/material/Button';
import Sidebar from '../../components/sidemenu/Sidebar';

export function DashboardTraining() {
  const [sceneNameList, setSceneNameList] = useState<{ key: string; value: string }[]>([]);
  const [sceneEndCount, setSceneEndCount] = useState<string>('');
  const [sceneCorrectRate, setSceneCorrectRate] = useState<string>('');

  const defaultFromDate = new Date().setHours(0, 0, 0, 0);
  const defaultToDate = new Date().setHours(23, 59, 59, 999);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [materialName, setMaterialName] = useState<string>('');
  const [sceneName, setSceneName] = useState<string>('');
  const [materialNameList, setMaterialNameList] = useState<{ key: string; value: string }[]>([]);
  const [selectedEndTypes, setSelectedEndTypes] = useState<string[]>([Const.EndTypes[0].name]);
  const orgId = GetOrgId();

  const dropdownDataUrl = `${import.meta.env.VITE_DATA_POOLS_URL}/selectDropdownData`;
  const rankingDataUrl = `${import.meta.env.VITE_DATA_POOLS_URL}/selectRankingData`;
  const correctCountDataUrl = `${import.meta.env.VITE_DATA_POOLS_URL}/selectCorrectNumberData`;
  const playTimeDataUrl = `${import.meta.env.VITE_DATA_POOLS_URL}/selectPlaytimeData`;
  const sceneEndCountDataUrl = `${import.meta.env.VITE_DATA_POOLS_URL}/selectCountSceneEndData`;
  const scenePointAverageDataUrl = `${
    import.meta.env.VITE_DATA_POOLS_URL
  }/selectScenePointAverageData`;
  const sceneScoreResultDataUrl = `${
    import.meta.env.VITE_DATA_POOLS_URL
  }/selectSceneScoreResultData`;
  const averageAnswerTimeDataUrl = `${
    import.meta.env.VITE_DATA_POOLS_URL
  }/selectAverageAnswerTimeData`;

  // React Router の useSearchParams フックを使用
  const [searchParams, setSearchParams] = useSearchParams();

  const dropDownParams = {
    [Const.KeyOrgID]: orgId,
  };
  const {
    data: dropDownData,
    loading: dropDownLoading,
    error: dropDownError,
  } = useGetDropdownDataAuto(dropdownDataUrl, dropDownParams);

  const dataParams = {
    [Const.KeyOrgID]: orgId,
    [Const.KeySceneName]: sceneName,
    [Const.KeyMaterialName]: materialName,
    [Const.KeyStartDatetimeUtc]: fromDate?.toJSON() || '',
    [Const.KeyEndDatetimeUtc]: toDate?.toJSON() || '',
    [Const.KeyEndType]: GetKeyEndTypes(selectedEndTypes),
  };

  const {
    data: rankingData,
    loading: rankingLoading,
    error: rankingError,
    execute: fetchRankingData,
  } = useGetDashboardData(rankingDataUrl, dataParams);
  const {
    data: correctCountData,
    loading: correctCountLoading,
    error: correctCountError,
    execute: fetchCorrectCountData,
  } = useGetDashboardData(correctCountDataUrl, dataParams);

  const {
    data: playTimeData,
    loading: playTimeLoading,
    error: playTimeError,
    execute: fetchPlayTimeData,
  } = useGetDashboardData(playTimeDataUrl, dataParams);

  const {
    data: sceneEndCountData,
    loading: sceneEndCountLoading,
    error: sceneEndCountError,
    execute: fetchSceneEndCountData,
  } = useGetDashboardData(sceneEndCountDataUrl, dataParams);

  const {
    data: scenePointAverageData,
    loading: scenePointAverageLoading,
    error: scenePointAverageError,
    execute: fetchScenePointAverageData,
  } = useGetDashboardData(scenePointAverageDataUrl, dataParams);

  const {
    data: sceneScoreResultData,
    loading: sceneScoreResultLoading,
    error: sceneScoreResultError,
    execute: fetchSceneScoreResultData,
  } = useGetDashboardData(sceneScoreResultDataUrl, dataParams);

  const {
    data: averageAnswerTimeData,
    loading: averageAnswerTimeLoading,
    error: averageAnswerTimeError,
    execute: fetchAverageAnswerTimeData,
  } = useGetDashboardData(averageAnswerTimeDataUrl, dataParams);

  // 教材プルダウンの設定
  useEffect(() => {
    if (Array.isArray(dropDownData) && dropDownData.length > 0) {
      // const uniqueData = [
      //   ...new Set(dropDownData.map((item: any) => item[Const.KeyMaterialName])),
      // ].map((materialName) => ({ key: materialName, value: materialName }));

      const dataMap = new Map<string, { key: string; value: string }>();
      dropDownData.forEach((item: any) => {
        const mapKey = `${item[Const.KeyMaterialName]}`;
        dataMap.set(mapKey, {
          key: String(item[Const.KeyMaterialName]),
          value: String(item[Const.KeyMaterialName]),
        });
      });
      setMaterialNameList(Array.from(dataMap.values()));
    } else {
      setMaterialNameList([]);
    }
  }, [dropDownData]);

  // シーンプルダウンの設定
  useEffect(() => {
    if (materialName && Array.isArray(dropDownData) && dropDownData.length > 0) {
      // const filteredScenes = [
      //   ...new Set(
      //     dropDownData
      //       .filter((item: any) => item.material_name === materialName)
      //       .map((item: any) => item.scene_name)
      //   ),
      // ];

      const filteredScenes = new Map<string, { key: string; value: string }>();
      dropDownData.forEach((item: any) => {
        if (item[Const.KeyMaterialName] === materialName) {
          const mapKey = `${item[Const.KeySceneName]}`;
          filteredScenes.set(mapKey, {
            key: String(item[Const.KeySceneName]),
            value: String(item[Const.KeySceneName]),
          });
        }
      });

      const sceneArray = Array.from(filteredScenes.values());
      setSceneName(sceneArray[0]?.value || '');
      setSceneNameList(sceneArray);
    } else {
      setSceneName('');
      setSceneNameList([]);
    }
  }, [materialName, dropDownData]);

  // intervalTimeが変更されるたびに再設定
  const [intervalTime, setIntervalTime] = useState(Const.RefreshDispalyMSTime);

  useEffect(() => {
    const interval = setInterval(() => {
      reloadData();
    }, intervalTime);

    return () => clearInterval(interval);
  }, [reloadData, intervalTime]);

  useEffect(() => {
    if (sceneName !== sceneName) {
      setSceneName(sceneName);
    }
    if (materialName !== materialName) {
      setMaterialName(materialName);
    }

    if (materialName && sceneName) {
      reloadData();
    }
    updateSearchParams();
  }, [materialName, sceneName, fromDate, toDate, selectedEndTypes]);

  useEffect(() => {
    if (sceneEndCountData) {
      setSceneEndCount(sceneEndCountData[0]?.count || 0);
    }
  }, [sceneEndCountData]);

  useEffect(() => {
    if (scenePointAverageData) {
      setSceneCorrectRate(scenePointAverageData[0]?.correct_question_avg || 0);
    }
  }, [scenePointAverageData]);

  function reloadData() {
    console.log('reloadData');
    console.log('materialName:', materialName);
    console.log('sceneName:', sceneName);

    if (materialName === '' || sceneName === '') {
      console.log('Either materialName or sceneName is empty. Aborting reload.');
      return;
    }

    // グラフのデータ再読み込み処理
    fetchRankingData();
    fetchCorrectCountData();
    fetchPlayTimeData();
    fetchSceneEndCountData();
    fetchScenePointAverageData();
    fetchSceneScoreResultData();
    fetchAverageAnswerTimeData();
  }

  // URL のクエリパラメータを検索項目に反映
  useEffect(() => {
    const urlParamMaterialName = searchParams.get('materialName') || '';
    const urlParamSceneName = searchParams.get('sceneName') || '';
    const urlParamStartDateTime = searchParams.get('startDateTime') || '';
    const urlParamToDateTime = searchParams.get('toDateTime') || '';
    const urlParamEndType = searchParams.get('endType') || '';
    setMaterialName(urlParamMaterialName);
    setSceneName(urlParamSceneName);
    setFromDate(
      urlParamStartDateTime ? new Date(urlParamStartDateTime) : new Date(defaultFromDate)
    );
    setToDate(urlParamToDateTime ? new Date(urlParamToDateTime) : new Date(defaultToDate));
    setSelectedEndTypes(urlParamEndType ? urlParamEndType.split(',') : selectedEndTypes);
  }, [searchParams]);

  // 入力内容を URL に追加
  const updateSearchParams = () => {
    const params = new URLSearchParams();
    params.set('materialName', materialName);
    params.set('sceneName', sceneName);
    if (fromDate) params.set('startDateTime', fromDate.toISOString());
    if (toDate) params.set('toDateTime', toDate.toISOString());

    // endTypeをカンマ区切りの文字列で設定
    if (selectedEndTypes.length > 0) {
      params.set('endType', selectedEndTypes.join(',')); // 配列をカンマ区切りの文字列に変換
    }

    // URLクエリパラメータを更新
    setSearchParams(params);
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerStyle = import.meta.env.VITE_ENV_VERSION !== 'prod' ? '#111827' : '#00B0B9';

  if (dropDownLoading) return <p>Loading...</p>;
  if (dropDownError) {
    const errorMessage =
      dropDownError instanceof Error ? dropDownError.message : 'An unknown error occurred';
    return <p>Error: {errorMessage}</p>;
  }

  return (
    <>
      {' '}
      <Sidebar />
      <div
        style={{
          marginLeft: 40,
          width: 'calc(100% - 40px)',
          transition: 'width 0.3s, margin-left 0.3s',
        }}
        className="grid grid-cols-1"
      >
        {/* <div style={{ width: '100%' }} className="grid grid-cols-1"> */}
        <div
          style={{
            position: 'fixed',
            top: '75px',
            zIndex: 999,
            // width: '100%',
            width: 'calc(100% - 40px)',
          }}
        >
          <div className="flex 3xl:hidden justify-end p-2">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded text-white"
              aria-label="検索条件"
              style={{ backgroundColor: headerStyle }}
            >
              <svg width="50" height="50" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 12h34M8 25h34M8 38h34" />
              </svg>
            </button>
          </div>
          <div
            className={`
              ${isMenuOpen ? 'block' : 'hidden'}
              3xl:block
              bg-white
            `}
          >
            <div className="p-4">
              <div
                className="p-2"
                style={{
                  borderWidth: '1px',
                  backgroundColor: `${Const.SearchItemColor}`,
                }}
              >
                <div className="flex 3xl:flex-row flex-col gap-8 p-2 mb-0">
                  <Dropdown
                    data={materialNameList}
                    label={Const.LabelMaterialName}
                    selected={materialName}
                    onChange={(selected: string) => setMaterialName(selected)}
                    disabled={false}
                  />
                  <Dropdown
                    data={Array.from(sceneNameList.values())}
                    label={Const.LabelSceneName}
                    selected={sceneName}
                    onChange={(selected: string) => setSceneName(selected)}
                    disabled={false}
                  />
                  <div className="flex items-center">
                    <label className="text-base font-bold">{Const.LabelStartDatetimeUtc}</label>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        format="yyyy/MM/dd HH:mm"
                        value={fromDate}
                        onChange={setFromDate}
                        onClose={() => updateSearchParams()}
                        ampm={false}
                        slotProps={{
                          actionBar: { actions: ['accept'] },
                          textField: {
                            size: 'small',
                            style: {
                              borderColor: `${Const.SearchItemBorderColor}`,
                              width: '200px',
                            },
                            onBlur: () => {
                              updateSearchParams();
                            },
                          },
                        }}
                        slots={{
                          actionBar: (props) => (
                            <>
                              <Button onClick={() => setFromDate(new Date())}>現在時刻</Button>
                              {props.actions?.map((action) => {
                                switch (action) {
                                  case 'accept':
                                    return (
                                      <Button
                                        key="accept"
                                        onClick={() => {
                                          props.onAccept?.();
                                          updateSearchParams();
                                        }}
                                      >
                                        OK
                                      </Button>
                                    );
                                  default:
                                    return null;
                                }
                              })}
                            </>
                          ),
                        }}
                      />
                    </LocalizationProvider>
                    <label className="text-base font-bold ml-4">{Const.LabelToDatetimeUtc}</label>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        format="yyyy/MM/dd HH:mm"
                        value={toDate}
                        onChange={setToDate}
                        onClose={() => updateSearchParams()}
                        ampm={false}
                        slotProps={{
                          actionBar: { actions: ['accept'] },
                          textField: {
                            size: 'small',
                            style: {
                              borderColor: `${Const.SearchItemBorderColor}`,
                              width: '200px',
                            },
                            onBlur: () => {
                              updateSearchParams();
                            },
                          },
                        }}
                        slots={{
                          actionBar: (props) => (
                            <>
                              <Button onClick={() => setToDate(new Date())}>現在時刻</Button>
                              {props.actions?.map((action) => {
                                switch (action) {
                                  case 'accept':
                                    return (
                                      <Button key="accept" onClick={props.onAccept}>
                                        OK
                                      </Button>
                                    );
                                  default:
                                    return null;
                                }
                              })}
                            </>
                          ),
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                  <DropdownMulti
                    label={Const.LabelEndType}
                    data={Const.EndTypes}
                    selected={selectedEndTypes}
                    onChange={(selected: string[]) => {
                      setSelectedEndTypes(selected);
                      console.log('Selected items:', selected);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <GraphAreaTraining
          sceneEndCount={sceneEndCount}
          sceneCorrectRate={sceneCorrectRate}
          rankingData={rankingData}
          sceneScoreResultData={sceneScoreResultData}
          playTimeData={playTimeData}
          correctCountData={correctCountData}
          averageAnswerTimeData={averageAnswerTimeData}
        ></GraphAreaTraining>
      </div>
    </>
  );
}

export default DashboardTraining;
